import { Component, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ProductionStatus } from "@enums/productionStatus.enum";
import { Department } from "@enums/department.enum";
import { GetProductionStatusColor } from "@helpers/getProductionStatusColor";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { UserProfile } from "@interfaces/userProfile.interface";
import { WorkOrder } from "@interfaces/workOrder.interface";
import { MachineService } from "@services/machine.service";

type DeptStatus = "green" | "supervisorrequired" | "breakdown";

type DeptOverview = {
	breakdownMachineCodes: string[];
	departmentName: string;
	deptKey: string;
	machines: any[];
	status: DeptStatus;
	supervisorMachineCodes: string[];
};

type BreakdownMemoryItem = {
	active: boolean; // currently breakdown in latest poll
	deptKey: string;
	lastSeenMs: number;
	machineCode: string; // ALWAYS cellNumber
	startMs: number; // first time breakdown began (used for 15s impact)
};

type AssistMemoryItem = {
	active: boolean;
	deptKey: string;
	lastSeenMs: number;
	machineCode: string; // cellNumber
};

@Component({
	encapsulation: ViewEncapsulation.None,
	moduleId: module.id,
	selector: "andon",
	styleUrls: ["andon.css"],
	templateUrl: "andon.html"
})

export class AndonComponent {
	private readonly BREAKDOWN_IMPACT_MS = 15000;
	private readonly BREAK_SCHEDULE_FRIDAY = [657, 700, 1000, 1012, 1015, 1255, 1300];
	private readonly BREAK_SCHEDULE_REGULAR = [657, 700, 915, 927, 930, 1230, 1257, 1300, 1555, 1600];
	private readonly DEPT_ID_TO_KEY: Record<number, string> = {
		[Department.BackOffice]: "BACK OFFICE",
		[Department.PressBrake]: "PRESS",
		[Department.PressShop]: "PRESS",
		[Department.FlatLaser]: "LASER",
		[Department.Laser]: "LASER",
		[Department.Assembly]: "ASSEMBLY",
		[Department.Welding]: "WELD",
		[Department.Firewall]: "FIREWALL",
		[Department.QualityControl]: "QUALITY",
		[Department.Outsource]: "OUTSOURCE",
		[Department.ToolRoom]: "TOOL ROOM"
	};
	private assistMemory = new Map<string, AssistMemoryItem>();
	private audioBreakdown = new Audio();
	private audioBreaktime = new Audio();
	private audioNoOperator = new Audio();
	private audioReady = false;
	private audioSupervisor = new Audio();
	private breakTimeLastDay = -1;
	private breakTimePlayed = new Set<number>();
	// Breakdown memory (KEYED BY cellNumber ONLY)
	breakdownCount = 0;
	private breakdownMemory = new Map<string, BreakdownMemoryItem>();
	departmentOverview: DeptOverview[] = [];

	private expandTimer: any = null;
	expandedDept: DeptOverview | null = null;
	private inExpandCycle = false;
	isLoading = false;
	// Stop repeated expanding
	private lastExpandedAssistStateByDept = new Map<string, boolean>();
	private lastExpandedBreakdownStartByDept = new Map<string, number>();
	lastRefresh = "";
	machines: any[] = [];
	machinesNoJob: any[] = [];
	machinesRunning: any[] = [];
	overlayExpanded = false;
	overlayStart: { height: number; left: number; top: number; width: number } | null = null;
	prodAct = 0;
	prodAmb = 0;
	prodRed = 0;
	showPopup = false;
	private shrinkTimer: any = null;
	siteId = 0;
	statusLookup: any[] = [];
	superRequiredCount = 0;
	workOrder: WorkOrder = new WorkOrder();

	constructor(private route: ActivatedRoute, private machineService: MachineService) {
		this.statusLookup = GetPropertiesFromEnum(ProductionStatus);
		this.route.params.subscribe((params) => {
			this.siteId = params.id;
			this.isLoading = true;
			this.showPopup = true;
			this.initAudio();
			this.initBreakTimeScheduler();
			this.getWo();
			setTimeout(() => {
				this.isLoading = false;
				this.showPopup = false;
			}, 2000);
		});

		setInterval(() => this.getWo(), 5000);
	}

	private buildDepartmentOverview(): void {
		const now = Date.now();
		const map = new Map<string, DeptOverview>();

		for (const machine of this.machines) {
			const deptKey = this.getDepartmentKeyFromMachine(machine);
			if (!deptKey) continue; // excludes BackOffice + Outsource

			let group = map.get(deptKey);
			if (!group) {
				group = {
					breakdownMachineCodes: [],
					departmentName: deptKey,
					deptKey,
					machines: [],
					status: "green",
					supervisorMachineCodes: [],
				};
				map.set(deptKey, group);
			}

			group.machines.push(machine);

			// Assistance pills: push ALL AssistanceRequired machines
			if (machine.productionStatus === ProductionStatus.AssistanceRequired) {
				const code = this.getMachineCode(machine);
				if (code && !group.supervisorMachineCodes.includes(code)) {
					group.supervisorMachineCodes.push(code);
				}
			}
		}

		// Attach breakdown pills from memory (ALL machines in that dept)
		// And compute 15s impact (dept turns red only during impact window)
		for (const group of map.values()) {
			let breakdownImpact = false;

			for (const item of this.breakdownMemory.values()) {
				if (item.deptKey !== group.deptKey) continue;

				// item is always active now because we delete recovered ones
				if (!group.breakdownMachineCodes.includes(item.machineCode)) {
					group.breakdownMachineCodes.push(item.machineCode);
				}

				if (now - item.startMs <= this.BREAKDOWN_IMPACT_MS) {
					breakdownImpact = true;
				}
			}

			if (breakdownImpact) group.status = "breakdown";
			else if (group.supervisorMachineCodes.length > 0) group.status = "supervisorrequired";
			else group.status = "green";
		}


		this.departmentOverview = Array.from(map.values());
	}

	private checkBreakTimeSounds(): void {
		const now = new Date();
		const today = now.getDate();

		// Reset played set at the start of each day
		if (today !== this.breakTimeLastDay) {
			this.breakTimePlayed.clear();
			this.breakTimeLastDay = today;
		}

		const isFriday = now.getDay() === 5;
		const schedule = isFriday ? this.BREAK_SCHEDULE_FRIDAY : this.BREAK_SCHEDULE_REGULAR;
		const hhmm = now.getHours() * 100 + now.getMinutes();

		for (const time of schedule) {
			if (hhmm === time && !this.breakTimePlayed.has(time)) {
				this.breakTimePlayed.add(time);
				this.playBreaktimeAudio();
				break;
			}
		}
	}

	formatDate(dateComplete: Date) {
		const date = new Date(dateComplete);
		return `${date.getDay()}/${date.getMonth()}/${date.getFullYear()}`;
	}

	formatUser(user: UserProfile) {
		const firstInitial = user.firstName.substring(0, 1);
		const secondInitial = user.lastName.substring(0, 1);
		return firstInitial + secondInitial;
	}

	getDepartmentIconClass(departmentName: string): string {
		if (!departmentName) return "fa-solid fa-industry";
		const name = departmentName.toLowerCase();

		if (name.includes("laser")) return "fas fa-bolt";
		if (name.includes("press")) return "fas fa-stamp";
		if (name.includes("weld")) return "fas fa-sparkles";
		if (name.includes("assy") || name.includes("assembly")) return "fas fa-tools";
		if (name.includes("fire")) return "fas fa-shield-alt";
		if (name.includes("quality")) return "fas fa-badge-check";
		if (name.includes("goods") || name.includes("stores")) return "fas fa-boxes-stacked";
		return "fas fa-industry";
	}

	private getDepartmentKeyFromMachine(machine: any): string {
		const deptId = Number(machine?.department);

		// keep old behaviour: ignore back office + outsource completely
		if (deptId === Department.BackOffice || deptId === Department.Outsource) return "";

		return this.DEPT_ID_TO_KEY[deptId] ?? `DEPT_${Number.isFinite(deptId) ? deptId : "Unknown"}`;
	}

	private getMachineCode(machine: any): string {
		return String(machine?.cellNumber ?? "").trim();
	}

	getWo() {
		this.machineService.app_GetAll().subscribe(
			(res: any) => {
				const tempMachines = Array.isArray(res) ? res : res?.response;
				const machines = tempMachines.filter((x: { siteId: number; }) => x.siteId == this.siteId);
				this.machines = machines || [];

				// Running/NoJob inferred from productionStatus only
				this.machinesRunning = this.machines.filter((x) => x.productionStatus === ProductionStatus.Active);
				this.machinesNoJob = this.machines.filter((x) => x.productionStatus !== ProductionStatus.Active);

				const prevSuper = this.superRequiredCount;
				this.superRequiredCount = this.machines.filter(
					(x) => x.productionStatus === ProductionStatus.AssistanceRequired
				).length;
				if (this.superRequiredCount > prevSuper) this.playSupervisorAudio();

				const prevBreakdown = this.breakdownCount;
				this.breakdownCount = this.machines.filter(
					(x) => x.productionStatus === ProductionStatus.Breakdown
				).length;
				if (this.breakdownCount > prevBreakdown) this.playBreakdownAudio();

				// Update breakdown memory first
				this.updateBreakdownMemory();

				this.lastRefresh = new Date().toLocaleString();
				this.renderStatusOverview();
				this.buildDepartmentOverview();

				this.updateAssistMemoryAndMaybeExpand();

				// Expand only on new events (optional, keep if you use overlay)
				this.maybeTriggerFullscreenAlertOnce();
			},
			(err) => {
				console.log(err);
				// this.toastService.error("Something Went Wrong!");
			}
		);
	}

	private initAudio(): void {
		if (this.audioReady) return;

		this.audioSupervisor.src = "../../../../../assets/AndonSupervisor.wav";
		this.audioBreakdown.src = "../../../../../assets/AndonBreakdown.mp3";
		this.audioNoOperator.src = "../../../../../assets/NoOperator.mp3";
		this.audioBreaktime.src = "../../../../../assets/AndonBreaktime.mp3";

		this.audioSupervisor.preload = "auto";
		this.audioBreakdown.preload = "auto";
		this.audioNoOperator.preload = "auto";
		this.audioBreaktime.preload = "auto";

		this.audioSupervisor.load();
		this.audioBreakdown.load();
		this.audioNoOperator.load();
		this.audioBreaktime.load();

		this.audioReady = true;
	}

	private initBreakTimeScheduler(): void {
		setInterval(() => this.checkBreakTimeSounds(), 15000);
	}

	private maybeTriggerFullscreenAlertOnce(): void {
		if (this.inExpandCycle) return;

		const now = Date.now();

		// --- keep assist guard state synced every poll ---
		// (prevents missing false->true transitions after a breakdown cycle)
		this.syncAssistStateGuardsIfMissing();

		// 1) breakdown (priority): newest startMs still within 15s
		let best: { deptKey: string; startMs: number } | null = null;
		for (const item of this.breakdownMemory.values()) {
			if (!item.active) continue;
			if (now - item.startMs > this.BREAKDOWN_IMPACT_MS) continue;
			if (!best || item.startMs > best.startMs) best = { deptKey: item.deptKey, startMs: item.startMs };
		}

		if (best) {
			const last = this.lastExpandedBreakdownStartByDept.get(best.deptKey);
			if (last !== best.startMs) {
				this.lastExpandedBreakdownStartByDept.set(best.deptKey, best.startMs);
				this.triggerAlertExpand(best.deptKey, "breakdown");
			}
			return;
		}

		// 2) assist: only when dept becomes orange (false -> true)
		for (const dept of this.departmentOverview) {
			const isAssist = dept.status === "supervisorrequired";
			const wasAssist = this.lastExpandedAssistStateByDept.get(dept.deptKey) ?? false;

			if (isAssist && !wasAssist) {
				this.lastExpandedAssistStateByDept.set(dept.deptKey, true);
				this.triggerAlertExpand(dept.deptKey, "supervisorrequired");
				return;
			}

			this.lastExpandedAssistStateByDept.set(dept.deptKey, isAssist);
		}
	}

	playBreakdownAudio() {
		this.initAudio();
		this.playSound(this.audioBreakdown);
	}

	playBreaktimeAudio(): void {
		this.initAudio();
		this.playSound(this.audioBreaktime);
	}

	private playSound(a: HTMLAudioElement): void {
		try {
			a.pause();
			a.currentTime = 0;
			const p = a.play();
			if (p && typeof (p as any).catch === "function") (p as Promise<void>).catch(() => { console.log("Promise Failed"); });
		} catch { console.log("Error Playing Sound."); }
	}

	playSupervisorAudio() {
		this.initAudio();
		this.playSound(this.audioSupervisor);
	}

	renderStatusColor(status: ProductionStatus) {
		return GetProductionStatusColor(status);
	}

	renderStatusName(status: ProductionStatus) {
		if (status == 100) return "SUPERVISOR";
		return this.statusLookup.filter((x) => x.id == status)[0].name.toUpperCase();
	}

	renderStatusOverview() {
		this.prodAct = 0;
		this.prodAmb = 0;
		this.prodRed = 0;

		this.machines.forEach((machine) => {
			if (
				(machine.productionStatus > 11 && machine.productionStatus < 41) ||
				machine.productionStatus === 120
			) {
				this.prodRed = this.prodRed + 1;
			}
			if (machine.productionStatus === 10) this.prodAct = this.prodAct + 1;
			if (machine.productionStatus > 39 && machine.productionStatus < 101) {
				this.prodAmb = this.prodAmb + 1;
			}
		});
	}

	private syncAssistStateGuardsIfMissing(): void {
		for (const dept of this.departmentOverview) {
			if (!this.lastExpandedAssistStateByDept.has(dept.deptKey)) {
				this.lastExpandedAssistStateByDept.set(dept.deptKey, dept.status === "supervisorrequired");
			}
		}
	}

	private triggerAlertExpand(deptKey: string, type: "breakdown" | "supervisorrequired") {
		if (this.inExpandCycle) return;

		const el = document.querySelector(`[data-deptkey="${deptKey}"]`) as HTMLElement | null;
		if (!el) return;

		const r = el.getBoundingClientRect();
		this.overlayStart = { height: r.height, left: r.left, top: r.top, width: r.width };

		const dept = this.departmentOverview.find((d) => d.deptKey === deptKey);
		if (!dept) return;

		// Overlay uses dept data but forces status to event type
		this.expandedDept = { ...dept, status: type as any };

		this.inExpandCycle = true;
		if (this.expandTimer) clearTimeout(this.expandTimer);
		if (this.shrinkTimer) clearTimeout(this.shrinkTimer);

		this.overlayExpanded = false;

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this.overlayExpanded = true;

				this.expandTimer = setTimeout(() => {
					this.overlayExpanded = false;

					this.shrinkTimer = setTimeout(() => {
						this.expandedDept = null;
						this.overlayStart = null;
						this.inExpandCycle = false;
					}, 650);
				}, 15000);
			});
		});
	}



	private updateAssistMemoryAndMaybeExpand(): void {
		if (this.inExpandCycle) return;

		const now = Date.now();
		const seenThisPoll = new Set<string>();

		for (const machine of this.machines) {
			if (machine.productionStatus !== ProductionStatus.AssistanceRequired) continue;

			const code = this.getMachineCode(machine);
			if (!code) continue;

			const deptKey = this.getDepartmentKeyFromMachine(machine);
			if (!deptKey) continue;

			seenThisPoll.add(code);

			const existing = this.assistMemory.get(code);

			if (!existing || existing.active === false) {
				this.assistMemory.set(code, { active: true, deptKey, lastSeenMs: now, machineCode: code });
				this.triggerAlertExpand(deptKey, "supervisorrequired");
				return; // only one expand per poll
			}

			existing.deptKey = deptKey;
			existing.lastSeenMs = now;
			existing.active = true;
			this.assistMemory.set(code, existing);
		}

		// mark unseen assists inactive (but keep record so we can detect re-activation)
		for (const item of this.assistMemory.values()) {
			if (!seenThisPoll.has(item.machineCode)) item.active = false;
		}
	}

	private updateBreakdownMemory(): void {
		const now = Date.now();
		const seenThisPoll = new Set<string>();

		for (const machine of this.machines) {
			if (machine.productionStatus !== ProductionStatus.Breakdown) continue;

			const machineCode = this.getMachineCode(machine);
			if (!machineCode) continue;

			const deptKey = this.getDepartmentKeyFromMachine(machine);
			if (!deptKey) continue;

			seenThisPoll.add(machineCode);

			const existing = this.breakdownMemory.get(machineCode);
			if (!existing) {
				this.breakdownMemory.set(machineCode, {
					active: true,
					deptKey,
					lastSeenMs: now,
					machineCode,
					startMs: now
				});
			} else {
				existing.deptKey = deptKey;
				existing.lastSeenMs = now;
				existing.active = true;
				this.breakdownMemory.set(machineCode, existing);
			}
		}

		for (const code of Array.from(this.breakdownMemory.keys())) {
			if (!seenThisPoll.has(code)) this.breakdownMemory.delete(code);
		}
	}
}
